// Vendor Imports
import _ from 'lodash';
import React, { Component } from 'react';
import classNames from 'classnames';
import { ForgeRadio, ForgeSelect } from '@tylertech/forge-react';

// Project Imports
import { getFirstActionableElement } from 'common/a11y';
import { I18n } from 'common/i18n';

// Constants
import {
  getSelectByFiscalYearStartAndEndDates,
  getSelectByYearStartAndEndDates,
  SINGLE_SELECT_BY,
} from 'common/dates';
import { FilterOrderConfig, FILTER_SORTING } from 'common/authoring_workflow/constants';
import { SoqlFilter } from './SoqlFilter';
import moment from 'moment';
import { FilterDataType } from './SoqlFilter';

const scope = 'shared.components.filter_bar.config';

export interface FilterConfigProps {
  filter: SoqlFilter;
  onUpdate: (filter: SoqlFilter) => void;
}

interface FilterConfigState {
  singleSelectBy: string;
  orderBy: FilterOrderConfig;
}

class FilterConfig extends Component<FilterConfigProps, FilterConfigState> {
  configElement = React.createRef<HTMLDivElement>();

  constructor(props: FilterConfigProps) {
    super(props);

    _.bindAll(this, ['onChangeSingleSelectBy', 'setFilterHidden', 'setFilterSingleValue']);
    const defaultFilterOrderBy = _.get(FILTER_SORTING[0], 'orderBy');
    this.state = {
      singleSelectBy: _.get(props, 'filter.singleSelect.by', 'day'),
      orderBy: _.get(props, 'filter.orderBy', defaultFilterOrderBy),
    };
  }

  componentDidMount(): void {
    const actionableElement = getFirstActionableElement(this.configElement.current);
    if (actionableElement) {
      actionableElement.focus();
    }
  }

  setFilterHidden(isHidden: boolean): () => void {
    return () => {
      const { filter, onUpdate } = this.props;
      const newFilter = _.merge({}, filter, {
        isHidden,
      });

      onUpdate(newFilter);
    };
  }

  setFilterSingleValue(bool: boolean): () => void {
    return () => {
      const { filter, onUpdate } = this.props;
      let newFilter;

      if (bool && this.isCalendarDateFilter()) {
        newFilter = _.merge({}, filter, {
          singleSelect: {
            by: this.state.singleSelectBy,
          },
        });
      } else {
        newFilter = _.merge({}, filter);

        if (bool) {
          _.set(newFilter, 'singleSelect', true);
        } else {
          _.unset(newFilter, 'singleSelect');
        }
      }

      onUpdate(newFilter);
    };
  }

  onChangeSingleSelectBy(newValue: string): void {
    const { filter, onUpdate } = this.props;
    const filterUpdate: { arguments?: { start: string; end: string }; singleSelect: { by: string } } = {
      singleSelect: {
        by: newValue,
      },
    };

    // Update start and end arguments
    if (filter.arguments && !Array.isArray(filter.arguments)) {
      let start, end;
      if (newValue === SINGLE_SELECT_BY.FISCAL_YEAR) {
        const endMoment = moment(filter.arguments.end);
        [start, end] = getSelectByFiscalYearStartAndEndDates(endMoment);
      } else {
        const startMoment = moment(filter.arguments.start).month(0).date(1);
        [start, end] = getSelectByYearStartAndEndDates(startMoment);
      }

      filterUpdate.arguments = { start, end };
    }
    const newFilter = _.merge({}, filter, filterUpdate);

    this.setState({
      singleSelectBy: newValue,
    });

    onUpdate(newFilter);
  }

  onChangeFilterSortBy = (newValue: FilterOrderConfig): void => {
    const { filter, onUpdate } = this.props;
    const newFilter = _.merge({}, filter, { orderBy: newValue });

    this.setState({ orderBy: newValue });

    onUpdate(newFilter);
  };

  isCalendarDateFilter(): boolean {
    return this.props.filter.dataTypeName === FilterDataType.DATE;
  }

  renderDateTypeDropdown(): JSX.Element | null {
    if (!this.isCalendarDateFilter()) {
      return null;
    }

    const dropdownProps = {
      disabled: !this.props.filter.singleSelect,
      onChange: (event: CustomEvent) => {
        this.onChangeSingleSelectBy(event.detail);
      },
      options: [
        { label: I18n.t('single_select_by_day', { scope }), value: SINGLE_SELECT_BY.DAY },
        { label: I18n.t('single_select_by_month', { scope }), value: SINGLE_SELECT_BY.MONTH },
        { label: I18n.t('single_select_by_year', { scope }), value: SINGLE_SELECT_BY.YEAR },
        { label: I18n.t('single_select_by_fiscal_year', { scope }), value: SINGLE_SELECT_BY.FISCAL_YEAR },
      ],
      density: 'small',
      value: this.state.singleSelectBy,
    };

    const fiscalYearWindow = _.get(window, 'socrata.fiscalYearConfig.fy_filters_enabled', false);
    if (!fiscalYearWindow) {
      // Remove fiscal year option if disabled by feature flag or admin panel config
      // Pushing it doesn't work because of typescript's aggressive implicit typing
      dropdownProps.options.pop();
    }

    return <ForgeSelect {...dropdownProps} />;
  }

  shouldRenderFilterSelection(): boolean {
    const { dataTypeName } = this.props.filter;
    return _.includes(
      [FilterDataType.COMPUTED, FilterDataType.DATE, FilterDataType.NUMBER, FilterDataType.TEXT],
      dataTypeName,
    );
  }

  shouldRenderFilterSortSelection(): boolean {
    return this.props.filter.dataTypeName === FilterDataType.TEXT;
  }

  renderFilterSelection(): JSX.Element {
    const { filter } = this.props;
    const singleValueContainerProps = {
      className: classNames(null, {
        'filter-single-value-date-container': this.isCalendarDateFilter(),
      }),
    };

    return (
      <form className="filter-options">
        <label>{I18n.t('filter_selection', { scope })}</label>
        <div className="radio-button-group" role="radiogroup">
          <ForgeRadio
            name="filter-selection-option-radio"
            id="filter-multiple-values"
            checked={!filter.singleSelect}
            onChange={this.setFilterSingleValue(false)}
          >
            {I18n.t('multiple_values', { scope })}
          </ForgeRadio>
          <div className="filter-single-value-container">
            <ForgeRadio
              name="filter-selection-option-radio"
              checked={!!filter.singleSelect}
              id="filter-single-value"
              onChange={this.setFilterSingleValue(true)}
              {...singleValueContainerProps}
            >
              {I18n.t('single_value', { scope })}
            </ForgeRadio>
            {this.renderDateTypeDropdown()}
          </div>
        </div>
      </form>
    );
  }

  renderFilterSortSelection = (): JSX.Element => {
    const { orderBy } = this.state;
    const options = _.map(FILTER_SORTING, (option) => {
      return {
        value: option.orderBy,
        label: option.title,
        leadingIcon: option.forgeIcon,
        leadingIconType: 'component',
      };
    });

    const filterSortDropdownAttributes = {
      id: 'filter-sorting-order-by-selection',
      'data-testid': 'filter-sorting-order-by-selection',
      onChange: (event: CustomEvent) => {
        this.onChangeFilterSortBy(event.detail);
      },
      options: options,
      value: orderBy,
    };

    return (
      <form className="filter-options">
        <label>{I18n.t('filter_sorting.title', { scope })}</label>
        <div>
          <ForgeSelect {...filterSortDropdownAttributes} />
          <div>{I18n.t('filter_sorting.description', { scope })}</div>
        </div>
      </form>
    );
  };

  render() {
    const { filter } = this.props;
    const filterSelection = this.shouldRenderFilterSelection() ? this.renderFilterSelection() : null;
    const filterSort = this.shouldRenderFilterSortSelection() ? this.renderFilterSortSelection() : null;

    return (
      <div className="filter-config" ref={this.configElement}>
        {filterSelection}
        <form className="filter-options">
          <label>{I18n.t('filter_visibility', { scope })}</label>
          <div className="radio-button-group" role="radiogroup">
            <ForgeRadio
              name="visibility-option-radio"
              id="filter-interactive"
              checked={!filter.isHidden}
              onChange={this.setFilterHidden(false)}
            >
              <span className="option-label">{I18n.t('interactive_label', { scope })}</span>
              <div className="setting-description">{I18n.t('interactive_description', { scope })}</div>
            </ForgeRadio>
            <ForgeRadio
              name="visibility-option-radio"
              checked={filter.isHidden}
              id="filter-hidden"
              onChange={this.setFilterHidden(true)}
            >
              <span className="option-label">{I18n.t('hidden_label', { scope })}</span>{' '}
              <div className="setting-description">{I18n.t('hidden_description', { scope })}</div>
            </ForgeRadio>
          </div>
        </form>
        {filterSort}
      </div>
    );
  }
}

export default FilterConfig;
